<template>
  <div>
    <LoadingSpinner :isLoading="loading" />
    <SoNote v-if="!loading && !mayEdit" tone="muted" icon="lock">{{ $t("securone.group_editor.no_write") }}</SoNote>

    <div v-else-if="!loading">
      <SoPageHead :title="isNew ? $t('securone.group_editor.new_title') : form.name || ''" :subtitle="isNew ? $t('securone.group_editor.new_subtitle') : $t('securone.group_editor.edit_subtitle')">
        <router-link :to="{ name: 'securone_groups' }" class="sd-btn sd-btn--secondary"><BaseIcon icon="chevron-left" class="mr-1" />{{ $t("securone.group_editor.back") }}</router-link>
      </SoPageHead>

      <ValidationObserver ref="form">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <div class="flex flex-col gap-4">
            <div class="sd-card px-5 py-4 flex flex-col gap-4">
              <div>
                <BaseInput v-model="form.name" :field_name="$t('securone.group_editor.name')" rules="required" :placeholder="$t('securone.group_editor.name_placeholder')" />
                <div class="text-xs text-gray-500 mt-1">{{ $t("securone.group_editor.rename_help") }}</div>
              </div>
              <ScopeSelector v-model="scope" wide="global" capability="globalGroup" :help="$t('securone.group_editor.scope_help')" />
            </div>
            <div class="sd-card px-5 py-4">
              <div class="text-sm font-semibold text-secondary-800 pb-2">{{ $t("securone.groups.linked_categories") }}</div>
              <div v-if="linked.length" class="flex flex-wrap gap-2">
                <router-link v-for="c in linked" :key="c._id" :to="{ name: 'securone_category_edit', params: { id: c._id } }" class="inline-flex items-center gap-1 rounded border border-secondary-300 bg-gray-100 px-2 py-0.5 text-xs text-gray-700 hover:border-primary-400">{{ c.name }}<BaseIcon icon="chevron-right" class="text-secondary-500 text-xxs" /></router-link>
              </div>
              <div v-else class="text-sm text-gray-400">{{ isNew ? $t("securone.group_editor.new_not_linked") : $t("securone.groups.not_linked") }}</div>
              <div class="text-xs text-gray-500 mt-2">{{ $t("securone.group_editor.links_on_category") }}</div>
            </div>
          </div>

          <div class="sd-card px-5 py-4">
            <div class="flex items-end justify-between gap-3 mb-2">
              <div class="text-sm font-semibold text-secondary-800">{{ $t("securone.group_editor.members") }}</div>
              <div class="text-sm text-gray-600"><span class="font-mono font-bold text-gray-800">{{ form.members.length }}</span> {{ $t("securone.group_editor.of_eligible", { n: eligible.users.length }) }} · <span class="font-mono">{{ form.smsContacts.length }}</span> SMS</div>
            </div>
            <SoSearch v-model="search" :placeholder="$t('securone.users.search')" width="100%" class="mb-2" />
            <div class="border border-secondary-200 rounded divide-y divide-secondary-100 overflow-y-auto" style="max-height: 480px">
              <!-- V2 + V3: the picker offers eligible users only, from the server -->
              <label v-for="u in filteredUsers" :key="u._id" class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50" :class="{ 'bg-secondary-50': form.members.includes(u._id) }">
                <input type="checkbox" class="form-checkbox h-4 w-4" :value="u._id" v-model="form.members" />
                <SoAvatar :name="u.name" size="sm" :muted="u.pending" />
                <div class="flex-1 min-w-0">
                  <div class="text-sm text-gray-800 truncate">{{ u.name }}</div>
                  <div class="text-xs text-gray-500 truncate">{{ u.level === "customer" ? $t("securone.level.customer") : u.departmentNames.join(", ") }}<template v-if="u.pending"> · {{ $t("securone.users.invited_pending") }}</template></div>
                </div>
              </label>
              <div v-if="filteredSms.length" class="bg-gray-50 px-3 py-1 text-xxs uppercase tracking-wider text-gray-500 font-bold">{{ $t("securone.group_editor.sms_section") }}</div>
              <label v-for="s in filteredSms" :key="s._id" class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50" :class="{ 'bg-secondary-50': form.smsContacts.includes(s._id) }">
                <input type="checkbox" class="form-checkbox h-4 w-4" :value="s._id" v-model="form.smsContacts" />
                <SoAvatar :name="s.name" size="sm" muted />
                <div class="flex-1 min-w-0">
                  <div class="text-sm text-gray-800 truncate">{{ s.name }}</div>
                  <div class="text-xs text-gray-500 font-mono">{{ s.phone }}</div>
                </div>
              </label>
              <div v-if="!filteredUsers.length && !filteredSms.length" class="px-3 py-4 text-sm text-gray-400">{{ $t("securone.users.none") }}</div>
            </div>
            <div class="text-xs text-gray-500 mt-2">{{ $t("securone.group_editor.eligible_note") }}</div>
            <div class="text-xs text-gray-500 mt-1">{{ $t("securone.group_editor.users_invited_under_users") }}</div>
          </div>
        </div>
      </ValidationObserver>

      <div class="flex flex-wrap items-center gap-3 mt-4">
        <button class="sd-btn sd-btn--success" :disabled="saving" @click="save">{{ isNew ? $t("securone.group_editor.create") : $t("securone.common.save") }}<BaseIcon icon="save" class="ml-1" /></button>
        <router-link :to="{ name: 'securone_groups' }" class="sd-btn sd-btn--secondary">{{ $t("securone.common.cancel") }}</router-link>
        <template v-if="!isNew">
          <div class="ml-auto text-xs text-gray-500 text-right" style="max-width: 60ch">{{ deleteHint }}</div>
          <button class="sd-btn sd-btn--danger" @click="confirmDelete"><BaseIcon icon="trash" class="mr-1" />{{ $t("securone.group_editor.delete") }}</button>
        </template>
      </div>
    </div>

    <LossPreviewDialog ref="loss" />
  </div>
</template>

<script>
import SoPageHead from "./components/SoPageHead.vue";
import SoNote from "./components/SoNote.vue";
import SoSearch from "./components/SoSearch.vue";
import SoAvatar from "./components/SoAvatar.vue";
import ScopeSelector from "./components/ScopeSelector.vue";
import LossPreviewDialog from "./components/LossPreviewDialog.vue";
import api from "./securone.api";
import { errorMessage, escapeHtml } from "./securone.helpers";

// D-33. Scope selector: global (customer users only, V5) or departments. The
// member picker offers eligible users per V2 plus every customer user per V3,
// from the server. Saving is an update in place (I2). Narrowing the scope
// first shows what would be dropped (V7) and saves only after confirmation.
export default {
  name: "SecurOneGroupEditor",
  components: { SoPageHead, SoNote, SoSearch, SoAvatar, ScopeSelector, LossPreviewDialog },
  data() {
    return {
      loading: true,
      saving: false,
      group: null,
      groups: [],
      categories: [],
      eligible: { users: [], smsContacts: [] },
      search: "",
      form: { name: "", members: [], smsContacts: [] },
      scope: { scope: "departments", departmentIds: [] },
    };
  },
  computed: {
    isNew() {
      return this.$route.name === "securone_group_new";
    },
    canWrite() {
      return this.$store.getters["securone/canWrite"];
    },
    mayEdit() {
      return this.isNew ? !!this.canWrite.group : !!(this.group && this.group.canEdit);
    },
    linked() {
      if (this.isNew || !this.group) return [];
      return this.categories.filter((c) => (c.groups || []).some((id) => String(id) === this.group._id));
    },
    filteredUsers() {
      const q = this.search.trim().toLowerCase();
      return q ? this.eligible.users.filter((u) => [u.name, u.email, ...u.departmentNames].join(" ").toLowerCase().includes(q)) : this.eligible.users;
    },
    filteredSms() {
      const q = this.search.trim().toLowerCase();
      return q ? this.eligible.smsContacts.filter((s) => [s.name, s.phone].join(" ").toLowerCase().includes(q)) : this.eligible.smsContacts;
    },
    scopeChanged() {
      if (!this.group) return false;
      const before = [this.group.scope, ...(this.group.departmentIds || []).map(String).sort()].join("|");
      const after = [this.scope.scope, ...(this.scope.departmentIds || []).map(String).sort()].join("|");
      return before !== after;
    },
    deleteHint() {
      const n = this.form.members.length;
      if (!this.linked.length) return this.$t("securone.group_editor.delete_hint_plain", { n });
      return this.$t("securone.group_editor.delete_hint_linked", { n, categories: this.linked.map((c) => c.name).join(", ") });
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [groups, categories] = await Promise.all([api.groups(), api.categories()]);
        this.groups = groups;
        this.categories = categories;
        if (this.isNew) {
          this.scope = { scope: "departments", departmentIds: [] };
        } else {
          this.group = groups.find((g) => g._id === this.$route.params.id) || null;
          if (!this.group) return this.$router.replace({ name: "securone_groups" });
          this.form = { name: this.group.name, members: (this.group.members || []).map(String), smsContacts: (this.group.smsContacts || []).map(String) };
          this.scope = { scope: this.group.scope, departmentIds: (this.group.departmentIds || []).map(String) };
        }
        await this.loadEligible();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loading = false;
      }
    },
    // The picker follows the scope being edited: for an unchanged existing
    // group the server answers from the stored scope, otherwise from the draft.
    async loadEligible() {
      if (this.scope.scope === "departments" && !this.scope.departmentIds.length) {
        this.eligible = { users: [], smsContacts: [] };
        return;
      }
      try {
        this.eligible = this.group && !this.scopeChanged ? await api.eligibleMembers(this.group._id) : await api.eligibleMembers(null, this.scope);
        // current members who are no longer eligible stay ticked and listed, so
        // the person sees what the preview will drop
        if (this.group) {
          const known = this.eligible.users.map((u) => u._id);
          const stale = this.form.members.filter((id) => !known.includes(id));
          if (stale.length) {
            const people = await api.users();
            this.eligible = { ...this.eligible, users: [...this.eligible.users, ...people.users.filter((u) => stale.includes(u._id))] };
          }
        }
      } catch (error) {
        this.handleError(error, errorMessage(this, error));
      }
    },
    async save() {
      if (!(await this.$refs.form.validate())) return;
      if (this.scope.scope === "departments" && !this.scope.departmentIds.length) return;
      this.saving = true;
      try {
        // V7: preview first, show what is dropped, save only on confirmation
        if (!this.isNew && this.scopeChanged) {
          const preview = await api.scopePreview(this.group._id, this.scope);
          if (preview.dropped.length || preview.droppedSms.length) {
            const confirmed = await this.$refs.loss.open(preview);
            if (!confirmed) return;
          }
        }
        const body = { ...this.form, scope: this.scope.scope, departmentIds: this.scope.departmentIds };
        const saved = this.isNew ? await api.createGroup(body) : await api.updateGroup(this.group._id, body);
        const dropped = saved.dropped ? saved.dropped.length + (saved.droppedSms || []).length : 0;
        this.$toasted.show(this.isNew ? this.$t("securone.group_editor.created_toast") : dropped ? this.$t("securone.group_editor.dropped_toast", { n: dropped }) : this.$t("securone.group_editor.saved_toast"), { type: "success", theme: "outline", position: "top-right", duration: 6000 });
        this.$store.dispatch("securone/loadContext", {});
        this.$router.push({ name: "securone_groups" });
      } catch (error) {
        this.handleError(error, errorMessage(this, error));
      } finally {
        this.saving = false;
      }
    },
    // Reach of a category counting every linked group except the one being deleted.
    reachWithout(category, groupId) {
      const others = this.groups.filter((g) => (category.groups || []).some((id) => String(id) === g._id) && g._id !== groupId);
      const people = new Set();
      others.forEach((g) => (g.members || []).forEach((m) => people.add(String(m))));
      return { people: people.size, groups: others.length };
    },
    confirmDelete() {
      const g = this.group;
      const rows = this.linked.map((c) => {
        const after = this.reachWithout(c, g._id);
        const afterText = after.groups ? this.$tc("securone.common.people_in_groups", after.groups, { people: after.people, groups: after.groups }) : `<span class="text-red-500">${this.$t("securone.common.nobody")}</span>`;
        return `<div class="flex justify-between gap-4 py-1 border-b border-secondary-100"><span>${escapeHtml(c.name)}</span><span class="text-gray-500">${this.$tc("securone.common.people_in_groups", c.reach.groups, { people: c.reach.people, groups: c.reach.groups })} → ${afterText}</span></div>`;
      }).join("");
      this.$modal.show("dialog", {
        title: `<span class="text-red-500">${this.$t("securone.group_editor.delete_title", { name: escapeHtml(g.name) })}</span>`,
        text: `<div class="text-sm text-gray-700 text-left">${this.$t("securone.group_editor.delete_consequence", { n: this.form.members.length, sms: this.form.smsContacts.length })}${this.linked.length ? " " + this.$t("securone.group_editor.delete_unlinks", { categories: escapeHtml(this.linked.map((c) => c.name).join(", ")) }) : " " + this.$t("securone.group_editor.delete_not_linked")}${rows ? `<div class="mt-3 text-xs text-gray-500">${this.$t("securone.group_editor.reach_afterwards")}</div><div class="text-xs mt-1">${rows}</div>` : ""}</div>`,
        buttons: [
          { title: `<div class="bg-success-500 text-white text-sm font-sans py-2">${this.$t("securone.common.cancel")}</div>`, handler: () => this.$modal.hide("dialog") },
          { title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t("securone.group_editor.delete")}</div>`, handler: () => { this.$modal.hide("dialog"); this.remove(); } },
        ],
      });
    },
    async remove() {
      try {
        await api.deleteGroup(this.group._id);
        this.$toasted.show(this.$t("securone.group_editor.deleted_toast"), { type: "success", theme: "outline", position: "top-right", duration: 6000 });
        this.$router.push({ name: "securone_groups" });
      } catch (error) {
        this.handleError(error, errorMessage(this, error));
      }
    },
  },
  watch: {
    scope: {
      deep: true,
      handler() {
        if (!this.loading) this.loadEligible();
      },
    },
  },
  created() {
    this.setPageTitle("SecurOne · " + this.$t("securone.nav.groups"), this.$route.name);
    this.load();
  },
};
</script>
